<script lang="ts">
    import {Flex, Spacer} from 'shared/layouts';
    import {KpPopover, type TogglePopoverFn} from 'shared/ui-widgets/popover';
    import type {SvelteComponentConstructor} from 'core/types';
    import type {TabButton, TabsetId} from 'shared/ui-widgets/tabset/types';
    import {getSearchFieldTypeFiltersContext} from '../search-field-type-filters.context';
    import {
        FilterableDatatype,
        type FilterableFieldTypeDefinition,
        type FilteredValue,
        type FilterSearchType
    } from '../types';
    import {onDestroy} from 'svelte';
    import {cleanup, exists, isFunction} from 'shared/utils/custom-utils';
    import {get} from 'svelte/store';
    import {fade} from 'svelte/transition';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import KpButtonTabs from 'shared/ui-widgets/tabset/KpButtonTabs.svelte';
    import RecordLinkFilter from './RecordLinkFilter.svelte';
    import {
        BooleanValueFilter,
        DateValueFilter,
        DatetimeRangeValueFilter,
        NumberValueFilter,
        TextValueFilter,
        CodebookValueFilter,
        RecordValueFilter
    } from './datatypeValueFilter';

    export let fieldDefinition: FilterableFieldTypeDefinition;
    export let forceSearchType: FilterSearchType | null = null;

    const context = getSearchFieldTypeFiltersContext();

    let filteredValues = get(context.filteredValues$);
    const filteredValuesSubscription = context.filteredValues$.subscribe((currentFilteredValues) => filteredValues = currentFilteredValues);

    const datatypeValueFilters: Record<FilterableDatatype, SvelteComponentConstructor> = {
        [FilterableDatatype.BOOLEAN]: BooleanValueFilter,
        [FilterableDatatype.DATE]: DateValueFilter,
        [FilterableDatatype.DATETIME]: DateValueFilter,
        [FilterableDatatype.DATETIME_RANGE]: DatetimeRangeValueFilter,
        [FilterableDatatype.NUMBER]: NumberValueFilter,
        [FilterableDatatype.TEXT]: TextValueFilter,
        [FilterableDatatype.CODEBOOK_VALUE]: CodebookValueFilter,
        [FilterableDatatype.RECORD]: RecordValueFilter
    };

    let popoverOpen = false;
    let togglePopover: TogglePopoverFn;

    const togglePopoverOnSearchSubscription = context.filteredValues$.subscribe(() => {
        if (isFunction(togglePopover)) {
            togglePopover(false);
        }
    });

    let activeTab: TabsetId | null = null;
    const setupTabsObject = setupTabs();
    const {tabButtons} = setupTabsObject;
    let {tabToSelect} = setupTabsObject;
    const initialTabToSelect = tabToSelect;

    const tabIdToFieldParamMap = {
        'tab-value': fieldDefinition.valueFilterSearchDefinition?.fieldName,
        'tab-target-reference': fieldDefinition.targetReferenceFilterSearchDefinition?.fieldName,
        'tab-origin-reference': fieldDefinition.originReferenceFilterSearchDefinition?.fieldName
    };

    onDestroy(() => {
        cleanup(filteredValuesSubscription, togglePopoverOnSearchSubscription);
    });

    function setupTabs(): { tabButtons: TabButton[]; tabToSelect: TabsetId } {
        const tabs: TabButton[] = [];
        let toSelect: TabsetId | null = null;

        if (exists(forceSearchType)) {
            activeTab = `tab-${forceSearchType}`;
            toSelect = activeTab;
            return {
                tabButtons: [{id: activeTab, label: 'Hledat'}],
                tabToSelect: toSelect
            };
        }

        if (exists(fieldDefinition.valueFilterSearchDefinition) && !isForcedFilter(fieldDefinition.valueFilterSearchDefinition.fieldName)) {
            tabs.push({id: 'tab-value', label: 'Hodnota'});
            toSelect = 'tab-value';
        }

        if (exists(fieldDefinition.originReferenceFilterSearchDefinition) && !isForcedFilter(fieldDefinition.originReferenceFilterSearchDefinition.fieldName)) {
            tabs.push({id: 'tab-origin-reference', label: 'Záznam'});
            toSelect = 'tab-origin-reference';
        }

        if (exists(fieldDefinition.targetReferenceFilterSearchDefinition) && !tabs.find((tab) => tab.id === 'tab-origin-reference') && !isForcedFilter(fieldDefinition.targetReferenceFilterSearchDefinition.fieldName)) {
            tabs.push({id: 'tab-target-reference', label: 'Záznam'});
            toSelect = 'tab-target-reference';
        }

        activeTab = toSelect;
        return {tabButtons: tabs, tabToSelect: toSelect};
    }

    function isForcedFilter(fieldName: string) {
        return exists(context.forcedFilterValues[fieldName]);
    }

    function isFilterActive(values: Record<string, FilteredValue>, ...filterParams: (string | undefined)[]): boolean {
        return filterParams.some((param) => exists(param) && (exists(values[param]) || exists(context.forcedFilterValues[param])));
    }

    const handleTabChange = (event: CustomEvent<TabsetId>) => {
        activeTab = event.detail;
    };

    const handlePopoverOpen = () => {
        popoverOpen = true;

        const firstActiveTab = tabButtons.find((tab) => isFilterActive(filteredValues, tabIdToFieldParamMap[tab.id]));

        if (exists(firstActiveTab)) {
            activeTab = firstActiveTab.id;
            tabToSelect = activeTab;
        } else {
            activeTab = initialTabToSelect;
            tabToSelect = activeTab;
        }
    };

    $: filterActive = isFilterActive(filteredValues, fieldDefinition.valueFilterSearchDefinition?.fieldName, fieldDefinition.targetReferenceFilterSearchDefinition?.fieldName, fieldDefinition.originReferenceFilterSearchDefinition?.fieldName);
</script>

{#if tabButtons.length > 0}
    <KpPopover additionalPopoverButtonClasses="field-type-filter-button {filterActive ? 'filter-active' : ''}"
               buttonSize="xs"
               buttonStyle="no-background"
               bind:togglePopover
               on:popover-open={handlePopoverOpen}
               on:popover-close={() => popoverOpen = false}>

        <svelte:fragment slot="button">
            <UIcon icon="filter"/>
        </svelte:fragment>

        <svelte:fragment slot="popover-content">
            {#if popoverOpen}
                <Flex direction="column">
                    {#if tabButtons.length > 1}
                        <small class="text-muted">Typ hledání</small>
                        <Spacer direction="vertical" size="s"/>

                        <KpButtonTabs {tabToSelect} {tabButtons} fillWidth on:tab-change={handleTabChange}>
                            <div class="custom-filter-tab" slot="customTab" let:tab>
                                {@const isActive = isFilterActive(filteredValues, tabIdToFieldParamMap[tab.id])}

                                {tab.label}

                                {#if isActive}
                                    <span class="filter-active-indicator"></span>
                                {/if}
                            </div>
                        </KpButtonTabs>

                        <Spacer direction="vertical" size="ml"/>
                    {/if}

                    <small class="text-muted">Hledat</small>
                    <Spacer direction="vertical" size="s"/>

                    {#key activeTab}
                        <div class="anim-container" in:fade={{duration: 250}}>
                            {#if activeTab === 'tab-value'}
                                {@const
                                    component = datatypeValueFilters[fieldDefinition.valueFilterSearchDefinition.datatype.name]}

                                <svelte:component this={component}
                                                  valueFilterDefinition={fieldDefinition.valueFilterSearchDefinition}/>
                            {/if}

                            {#if activeTab === 'tab-target-reference'}
                                <RecordLinkFilter linkType="target"
                                                  filterDefinition={fieldDefinition.targetReferenceFilterSearchDefinition}/>
                            {/if}

                            {#if activeTab === 'tab-origin-reference'}
                                <RecordLinkFilter linkType="origin"
                                                  filterDefinition={fieldDefinition.originReferenceFilterSearchDefinition}/>
                            {/if}
                        </div>
                    {/key}
                </Flex>
            {/if}
        </svelte:fragment>
    </KpPopover>
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .anim-container {
        display: flex;
        flex-direction: column;
    }

    .custom-filter-tab {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: @spacing-s;

        .filter-active-indicator {
            display: block;
            width: 8px;
            height: 8px;
            flex-shrink: 0;
            border-radius: 50%;
            background-color: var(--brand-orange);
        }
    }

    :global {
        .field-type-filter-button {
            display: flex;
            height: 24px;
        }
    }
</style>